import { api } from '../../lib/api';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';

/**
 * CONTATTI — tipi e query per la rubrica unica.
 *
 * API: /api/v1/contacts (apps/api/src/modules/contacts). Un soggetto compare
 * una volta sola con le sue etichette addosso: puo' essere insieme cliente,
 * fornitore e professionista, e la riga e' comunque una.
 *
 * Perche' una rotta nuova e non /clients: le tendine che scelgono il cliente
 * (trattative, progetti, amministrazione) leggono /clients aspettandosi SOLO
 * clienti, con un tetto di 200 righe. Mescolare li' dentro fornitori e
 * potenziali avrebbe fatto sparire righe dalle tendine senza nessun errore.
 */

export type TipoContatto = 'CLIENTE' | 'POTENZIALE' | 'PROFESSIONISTA' | 'FORNITORE';
export type FiltroTipo = 'tutti' | 'clienti' | 'potenziali' | 'professionisti';

export interface RigaContatto {
  key: string;
  kind: 'client' | 'supplier' | 'deal';
  id: string;
  name: string;
  tipi: TipoContatto[];
  email: string | null;
  phone: string | null;
  vat: string | null;
  city: string | null;
  sector: string | null;
  clientId: string | null;
  supplierId: string | null;
  referenti: number;
  ricavoYtd: number;
  canoneMensile: number;
  potenziale: number;
  trattativeAperte: number;
  progettiAttivi: number;
  scaduto: number;
  speso: number;
  daPagare: number;
  abbonamenti: number;
}

export interface ElencoContatti {
  items: RigaContatto[];
  total: number;
  conta: {
    tutti: number;
    clienti: number;
    potenziali: number;
    professionisti: number;
    doppi: number;
    senzaRecapito: number;
  };
}

export interface ReferenteDTO {
  id: string;
  fullName: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
}

export interface TrattativaDTO {
  id: string;
  title: string;
  amount: number;
  stage: string;
  outcome: 'APERTO' | 'VINTO' | 'PERSO';
  pipeline: string;
  expectedCloseDate: string | null;
  createdAt: string;
  projectId: string | null;
  projectCode: string | null;
}

export interface ContattoCompleto {
  key: string;
  kind: 'client' | 'supplier' | 'deal';
  id: string;
  name: string;
  tipi: TipoContatto[];
  anagrafica: {
    shortName: string;
    type: string;
    status: string;
    vat: string | null;
    taxCode: string | null;
    pec: string | null;
    sector: string | null;
    website: string | null;
    sinceYear: number | null;
    addressLine: string | null;
    city: string | null;
    zip: string | null;
    province: string | null;
    accountManager: { firstName: string; lastName: string; initials: string } | null;
    tags: { id: string; name: string }[];
    hourlyRate?: number | null;
    dailyRate?: number | null;
    skills?: string[];
    notes?: string | null;
  };
  recapiti: { email: string | null; phone: string | null };
  referenti: ReferenteDTO[];
  trattative: TrattativaDTO[];
  progetti: Array<{ id: string; code: string; name: string; status: string; division: string | null; deadline: string | null; progress: number; revenue: number }>;
  abbonamenti: Array<{ id: string; planLabel: string; type: string; provider: string; status: string; amount: number; billingCycle: string; currentPeriodEnd: string | null; externalUrl: string | null }>;
  canoni: Array<{ id: string; name: string; amountNet: number; cycle: string; startDate: string; endDate: string | null; nextDueDate: string; inVigore: boolean }>;
  canoneMensile: number;
  /** Gli abbonamenti riportati a mese (un annuale da 366 non e' 366 al mese). */
  mensileAbbonamenti: number;
  /** `count` sono tutti i documenti in archivio; `totale` e gli altri importi
   *  lasciano fuori le annullate e le note di credito, che non sono soldi.
   *  `scaduto` e `daIncassare` sono quanto ci deve, con la regola
   *  dell'Amministrazione: residuo delle rate delle fatture emesse dalla data
   *  di taglio (le fatture di oltre un anno non sono piu' crediti, 7/10/2026). */
  fatture: { count: number; annullate: number; totale: number; daIncassare: number; scaduto: number; incassato: number; incassatoYtd: number };
  ultimeFatture: Array<{ id: string; number: string | null; issueDate: string | null; dueDate: string | null; status: string; total: number; project: { code: string } | null }>;
  fornitore: {
    schede: Array<{ id: string; fullName: string; isPerson: boolean }>;
    righe: Array<{ id: string; number: string | null; issueDate: string | null; dueDate: string | null; total: number; status: string; description: string | null }>;
    count: number;
    annullate: number;
    totale: number;
    daPagare: number;
    saldo: number;
  } | null;
  potenziale: number;
}

export const contattiQuery = (tipo: FiltroTipo, search: string) => ({
  queryKey: ['contacts', tipo, search] as const,
  queryFn: () => {
    const p = new URLSearchParams();
    if (tipo !== 'tutti') p.set('tipo', tipo);
    if (search) p.set('search', search);
    const qs = p.toString();
    return api.get<ElencoContatti>(`/contacts${qs ? `?${qs}` : ''}`);
  },
});

export const contattoQuery = (kind: string, id: string) => ({
  queryKey: ['contact', kind, id] as const,
  queryFn: () => api.get<ContattoCompleto>(`/contacts/${kind}/${id}`),
});

/** Chi puo' scrivere in rubrica: stesso permesso del CRM (stessa sezione). */
export function canWriteContatti(user: { permissions: Array<{ module: string; level: string }> } | null): boolean {
  if (!user) return false;
  const lvl = user.permissions.find((p) => p.module === ModuleKey.CRM)?.level ?? PermissionLevel.NONE;
  return hasAtLeast(lvl as PermissionLevel, PermissionLevel.WRITE);
}

/** Le etichette come si leggono in pagina. */
export const TIPO_LABEL: Record<TipoContatto, string> = {
  CLIENTE: 'Cliente',
  POTENZIALE: 'Potenziale',
  PROFESSIONISTA: 'Professionista',
  FORNITORE: 'Fornitore',
};

export const FILTRI: Array<{ key: FiltroTipo; label: string; conta: keyof ElencoContatti['conta'] }> = [
  { key: 'tutti', label: 'Tutti', conta: 'tutti' },
  { key: 'clienti', label: 'Clienti', conta: 'clienti' },
  { key: 'potenziali', label: 'Potenziali', conta: 'potenziali' },
  { key: 'professionisti', label: 'Professionisti', conta: 'professionisti' },
];

/**
 * Cosa mostrare come "quanto vale" in lista. Non e' lo stesso numero per
 * tutti: di un cliente conta quello che ha fatturato quest'anno, di un
 * potenziale quello che potrebbe valere, di un professionista quello che gli
 * abbiamo pagato. Una colonna sola con tre significati sarebbe illeggibile,
 * quindi accanto al numero ci va sempre la parola che dice cos'e'.
 */
export function valoreDiRiga(r: RigaContatto): { label: string; value: number } {
  if (r.tipi.includes('CLIENTE')) return { label: 'fatturato YTD', value: r.ricavoYtd };
  if (r.tipi.includes('PROFESSIONISTA') || r.tipi.includes('FORNITORE')) return { label: 'pagato', value: r.speso };
  return { label: 'potenziale', value: r.potenziale };
}

/** Cosa c'e' in sospeso su questa riga: e' la colonna che fa decidere. */
export function sospesoDiRiga(r: RigaContatto): { testo: string; tone: 'alert' | 'warn' | 'mute' } | null {
  if (r.scaduto > 0) return { testo: `€ ${Math.round(r.scaduto).toLocaleString('it-IT')} scaduti`, tone: 'alert' };
  if (r.daPagare > 0) return { testo: `€ ${Math.round(r.daPagare).toLocaleString('it-IT')} da pagare`, tone: 'warn' };
  if (r.trattativeAperte > 0) return { testo: r.trattativeAperte === 1 ? '1 trattativa aperta' : `${r.trattativeAperte} trattative aperte`, tone: 'warn' };
  // "Non ha un recapito" NON sta qui: lo dice gia' la colonna del recapito con
  // il trattino, e il totale sta in cima alla lista. Ripeterlo su mezza
  // rubrica coprirebbe le cose che chiedono davvero di essere fatte.
  return null;
}
